import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import { useAuth } from "@/app/context/Auth/AuthContext";

import Loader from "../../components/Loader";
import FitCheckLoading from "./components/FitCheckLoading";
import FitCheckReport from "./components/FitCheckReport";
import SelectDSP from "./components/SelectDSP";
import { FitCheckAssessment, useCreateFitCheckMutation, useFitCheck } from "./config/api";

export type STEPS = "SELECT_DSP" | "LOADING" | "RESULTS";

interface Company {
	_id: string;
	company_name: string;
	linkedin_url: string;
	logo_url: string;
	company_website: string;
	company_industry: string;
	company_size_approx: string;
	headquarters: string;
	type: string;
	locations: string[];
	about_us_summary: string;
}

interface DateInfo {
	day: number;
	month: number;
	year: number;
}

interface JobLocation {
	city: string;
	region: string;
	country: string;
}

interface QualificationItem {
	bp: boolean;
	value: string;
	_id?: string;
}

interface JobDescription {
	lookingToHire: string;
	responsibilities: QualificationItem[];
	qualifications: QualificationItem[];
}

interface ExperienceQualification {
	minimumLevelOfExperience: string;
	preferredYearsOfExperience: number;
}

interface EducationQualification {
	minEducationLevel: string;
	preferredEducationLevel: string;
	relevantFieldOfStudies: string[];
	relevantMajors: string[];
}

interface Salary {
	payOption: string;
	minPay: number;
	maxPay: number;
	payCurrency: string;
	payFrequency: string;
}

export interface JobDetails {
	_id: string;
	jobProcessingType: string;
	companyName: string;
	company: Company;
	jobTitle: string;
	startingDate: DateInfo;
	deadlineDate: DateInfo;
	numberOfOpening: number;
	jobStreetAddress: string;
	jobLocation: JobLocation;
	employmentType: string;
	jobExpertise: string;
	jobDescription: JobDescription;
	experienceQualification: ExperienceQualification;
	skillsQualifications: QualificationItem[];
	educationQualification: EducationQualification;
	salary: Salary;
	jobStatus: string;
	additionalInformation: string;
	applicationProcess: string;
	lastUpdatedAt: string;
	closeAt: string;
}

interface FitCheckProps {
	jobDetails: JobDetails;
}

function FitCheck({ jobDetails }: FitCheckProps) {
	const [currentStep, setCurrentStep] = useState<STEPS>("SELECT_DSP");
	const [fitCheckData, setFitCheckData] = useState<FitCheckAssessment | null>(null);
	const [isCheckingExisting, setIsCheckingExisting] = useState(true);

	const { userData } = useAuth();

	// Check for existing FitCheck on mount
	const { data: existingFitCheck, isLoading: isLoadingExisting } = useFitCheck(userData?.userID || "", jobDetails._id);

	// Mutation to create new FitCheck
	const { mutate: createFitCheck } = useCreateFitCheckMutation();

	// Check for existing FitCheck on component mount
	useEffect(() => {
		if (!isLoadingExisting) {
			setIsCheckingExisting(false);

			if (existingFitCheck?.fitCheck) {
				// Existing FitCheck found, show results
				setFitCheckData(existingFitCheck.fitCheck);
				setCurrentStep("RESULTS");
			} else {
				// No existing FitCheck, show DSP selection
				setCurrentStep("SELECT_DSP");
			}
		}
	}, [existingFitCheck, isLoadingExisting]);

	function runFitCheck(resumeURL: string) {
		if (!userData) {
			toast.error("User data not available");
			return;
		}

		setCurrentStep("LOADING");

		const user = {
			firstName: userData.firstName,
			lastName: userData.lastName,
			email: userData.email,
			userId: userData.userID,
		};

		createFitCheck(
			{
				userId: userData.userID,
				jobId: jobDetails._id,
				resumeURL,
				user,
				jobDetails: jobDetails as unknown as Record<string, unknown>,
			},
			{
				onSuccess: (response) => {
					setFitCheckData(response.fitCheck);
					setCurrentStep("RESULTS");
					toast.success("FitCheck completed successfully!");
				},
				onError: (error: Error) => {
					setCurrentStep("SELECT_DSP");
					const errorMessage =
						(error as unknown as { response?: { data?: { message?: string } } })?.response?.data?.message ||
						"Failed to run FitCheck. Please try again.";
					toast.error(errorMessage);
				},
			},
		);
	}

	// Show initial loading state while checking for existing FitCheck
	if (isCheckingExisting) {
		return (
			<div className="flex items-center justify-center min-h-[400px]">
				<Loader />
			</div>
		);
	}

	return (
		<div>
			{currentStep === "SELECT_DSP" && <SelectDSP runFitCheck={runFitCheck} />}
			{currentStep === "LOADING" && <FitCheckLoading />}
			{currentStep === "RESULTS" && fitCheckData && <FitCheckReport fitCheck={fitCheckData} />}
		</div>
	);
}

export default FitCheck;
